import { PppcActivitySnapshot } from "@/services/pppcActivityLog";
import { PPPC_SERVICE_GROUPS, PppcServiceKey } from "@/types/pppc";

export type PppcChange = "added" | "removed" | "changed";

export interface PppcServiceChange {
  key: PppcServiceKey;
  label: string;
  groupLabel: string;
  change: PppcChange;
  before: "allow" | "deny" | "unset";
  after: "allow" | "deny" | "unset";
}

export interface PppcFieldChange<T = string> {
  field: "appName" | "identifier" | "identifierType" | "codeRequirement";
  label: string;
  before: T;
  after: T;
}

export interface PppcDiffResult {
  fieldChanges: PppcFieldChange[];
  serviceChanges: PppcServiceChange[];
  unchanged: boolean;
}

const SERVICE_LOOKUP: Record<string, { label: string; groupLabel: string }> = {};
for (const group of PPPC_SERVICE_GROUPS) {
  for (const svc of group.services) {
    SERVICE_LOOKUP[svc.key] = { label: svc.label, groupLabel: group.groupLabel };
  }
}

const choiceOf = (
  snap: PppcActivitySnapshot,
  key: string,
): "allow" | "deny" | "unset" => {
  const v = snap.choices?.[key];
  return v === "allow" || v === "deny" ? v : "unset";
};

export function diffPppcSnapshots(
  before: PppcActivitySnapshot,
  after: PppcActivitySnapshot,
): PppcDiffResult {
  const fieldChanges: PppcFieldChange[] = [];

  if (before.appName !== after.appName) {
    fieldChanges.push({
      field: "appName",
      label: "App name",
      before: before.appName,
      after: after.appName,
    });
  }
  if (before.identifier !== after.identifier) {
    fieldChanges.push({
      field: "identifier",
      label: "Identifier",
      before: before.identifier,
      after: after.identifier,
    });
  }
  if (before.identifierType !== after.identifierType) {
    fieldChanges.push({
      field: "identifierType",
      label: "Identifier type",
      before: before.identifierType,
      after: after.identifierType,
    });
  }
  if (before.codeRequirement !== after.codeRequirement) {
    fieldChanges.push({
      field: "codeRequirement",
      label: "Code Requirement",
      before: before.codeRequirement,
      after: after.codeRequirement,
    });
  }

  const allKeys = new Set<string>([
    ...Object.keys(before.choices ?? {}),
    ...Object.keys(after.choices ?? {}),
  ]);

  const serviceChanges: PppcServiceChange[] = [];
  for (const key of allKeys) {
    const a = choiceOf(before, key);
    const b = choiceOf(after, key);
    if (a === b) continue;
    let change: PppcChange;
    if (a === "unset") change = "added";
    else if (b === "unset") change = "removed";
    else change = "changed";
    const meta = SERVICE_LOOKUP[key] ?? { label: key, groupLabel: "Other" };
    serviceChanges.push({
      key: key as PppcServiceKey,
      label: meta.label,
      groupLabel: meta.groupLabel,
      change,
      before: a,
      after: b,
    });
  }

  // Sort: added → changed → removed, then by group + label
  const order: Record<PppcChange, number> = { added: 0, changed: 1, removed: 2 };
  serviceChanges.sort((x, y) => {
    if (order[x.change] !== order[y.change]) return order[x.change] - order[y.change];
    if (x.groupLabel !== y.groupLabel) return x.groupLabel.localeCompare(y.groupLabel);
    return x.label.localeCompare(y.label);
  });

  return {
    fieldChanges,
    serviceChanges,
    unchanged: fieldChanges.length === 0 && serviceChanges.length === 0,
  };
}
